import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { Button } from "../button";
import { Dialog } from "../dialog";
import { focusFirstInvalid } from "../fields";
import { StatusMessage } from "../status-message";
import { toast } from "../toaster";

// The one confirm dialog of the console: a person confirms a command that changes something, and the dialog keeps the rules every such
// dialog has (docs/engineering/admin-design-system.md, section 7):
//
//   1. Nothing is sent before the person confirms. Opening the dialog sends nothing; only the confirm button (or Enter in a field) does.
//   2. One idempotency key for each dialog instance. It is made once, on the first send, and kept in a ref. A retry after a failure sends the
//      SAME key, so a command that did run is not run twice. The key is renewed only when the next body may differ: after a refused field
//      (`keepForm`), after `rotateKey`, and after a `notDone` answer.
//   3. While a retry is possible the fields are LOCKED (`locked`): the body cannot change under the key.
//   4. While the command runs, Escape, Cancel and a second confirm do nothing. Focus stays inside the dialog: the confirm button is
//      `aria-disabled`, not `disabled`, so it keeps focus; after a failure that ends the dialog focus moves to the failure message.
//   5. A failure is shown inside the dialog, in words, with the alert role. It never looks like a success and never goes to a toast.
//   6. A success closes the dialog (`onDone`) and may show one polite toast (`successMessage`). An answer that arrives after the dialog is
//      gone does nothing (a `mounted` guard).
//
// The caller owns the words, the fields and the command. It passes `mutate(idempotencyKey)`, or its own `controller` (section "controller").

export type CommandFailure = Readonly<{
  /** What went wrong, in words. Never customer text. */
  message: string;
  /** An extra line under the message (for example what a refusal means for this action). */
  hint?: string | null | undefined;
  /** A small line under the message, for example `Reference for support: abc`. Never customer text. */
  footerNote?: ReactNode;
  /** The same command may be sent again with the same key: it may or may not have run. The fields are locked while this is true. */
  retryable: boolean;
  /** Keep the form although the command cannot be retried as it is (a field was refused: the person fixes it). The key is renewed. */
  keepForm?: boolean | undefined;
  /** Renew the idempotency key for the next send, even though the form stays as it is. */
  rotateKey?: boolean | undefined;
  /** The page behind is out of date: `onFailureReload` runs. */
  reload?: boolean | undefined;
  /** A stale screen is a `conflict`, not an error of the person. */
  tone?: "error" | "conflict" | undefined;
  /** A code of the caller, handed to `titleFor` (for example `"conflict"`). */
  kind?: string | undefined;
}>;

/** An answer that is NOT a success although the command ran (for example a reconcile whose provider did not answer). */
export type NotDone = Readonly<{
  message: string;
  hint?: string | null | undefined;
  footerNote?: ReactNode;
}>;

export type CommandResult<TValue = void> =
  | Readonly<{ ok: true; value: TValue }>
  | Readonly<{ ok: false; failure: CommandFailure }>
  /** The command ran and did not do its job. The dialog stays open with a NEW key; a retry is another command. */
  | Readonly<{ ok: false; notDone: NotDone }>;

/** The state of one command, for a page that keeps its own (Itinerary and Memory have a hook of their own). */
export type CommandController = Readonly<{
  failure: CommandFailure | null;
  notDone?: NotDone | null;
  pending: boolean;
  /** Runs the command. The dialog has already checked `validate`, `confirmDisabled`, `stale` and `pending`. */
  submit: () => unknown;
  /** A field was edited (only with `clearFailureOn="edit"`). */
  clearFailure?: () => void;
}>;

type CommandDialogState = Readonly<{ pending: boolean; locked: boolean }>;

type SharedProps = Readonly<{
  title: string;
  description?: ReactNode;
  /** Replaces the description once the dialog has ended (a final failure or a stale screen). `null` shows none. */
  endedDescription?: ReactNode;
  confirmLabel: string;
  /** The label of the confirm button while the command runs. */
  pendingLabel?: string;
  /** The label of the confirm button after a failure that may work on a second try. */
  retryLabel?: string;
  /** The label of the confirm button after a `notDone` answer. */
  notDoneRetryLabel?: string;
  cancelLabel?: string;
  /** The label of the second button once the dialog has ended or the command did not do its job ("Close", "Reload the plan"). */
  closeLabel?: string;
  /** `danger` makes the confirm button destructive. */
  tone?: "primary" | "danger";
  /** Which button comes first. Cancel first is the default; the request dialogs put the confirm button first. */
  order?: "cancel-first" | "confirm-first";
  /** The fields of the command. A function gets `{ pending, locked }`. The fields sit in a layout-neutral `<fieldset disabled>` while pending or locked. */
  children?: ReactNode | ((state: CommandDialogState) => ReactNode);
  /**
   * Checks the fields before the send. Return `false` to stop: the caller has shown its own field errors, and the first invalid control
   * takes focus. It is called on every confirm, so it can show all errors at once.
   */
  validate?: () => boolean;
  /** Keeps the confirm button off (native `disabled`) without moving focus, for example until a box is ticked. */
  confirmDisabled?: boolean;
  /** Says why the confirm button is off. Shown under the fields and tied to the button with `aria-describedby`. */
  confirmDisabledReason?: ReactNode;
  /** The screen behind is not the one this dialog was opened on. Nothing can be sent: the dialog says so and the confirm button is removed. */
  stale?: boolean;
  /** The sentence shown for a stale screen. */
  staleMessage?: string;
  /** Changes the title for a state (for example "This plan changed" for a conflict or a stale screen). Return `undefined` to keep `title`. */
  titleFor?: (
    state: Readonly<{ failure: CommandFailure | null; stale: boolean }>,
  ) => string | undefined;
  /** `"edit"`: a field edit clears a failure that left the form open (a refused field). Retryable failures lock the fields, so they stay. */
  clearFailureOn?: "edit" | "never";
  /** A line under the buttons, for example a reference for support. */
  footerNote?: ReactNode;
  /** A failure with `reload: true` arrived: the page behind reads again (the dialog stays open to say why). */
  onFailureReload?: ((failure: CommandFailure) => void) | undefined;
  /** A `notDone` answer arrived: the page behind reads again. */
  onReload?: (() => void) | undefined;
  onClose: () => void;
  /** A fixed sentence for a polite toast on success ("Request accepted"). */
  successMessage?: string | undefined;
  /** Classes for the dialog panel, for example `[--dialog-width:48rem]` for a wider one (two columns side by side). */
  dialogClassName?: string | undefined;
  /** Focus the heading when the dialog opens, so a screen reader reads the title and the description first (as `Dialog` does). */
  focusTitle?: boolean;
  /** Move focus to the failure message every time a command fails, not only when the dialog has ended. For a dialog whose fields stay open. */
  focusFailure?: boolean;
}>;

type CommandDialogProps<TValue> = SharedProps &
  (
    | Readonly<{
        /** Sends the command. Resolve with the result; never throw for an expected failure. */
        mutate: (idempotencyKey: string) => Promise<CommandResult<TValue>>;
        /** The command succeeded. Close the dialog here. */
        onDone: (value: TValue) => void;
        controller?: undefined;
      }>
    | Readonly<{
        /** The page's own command state. The dialog then sends nothing itself and keeps no key. */
        controller: CommandController;
        mutate?: undefined;
        onDone?: undefined;
      }>
  );

function newKey(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  return `cmd-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

type RunnerOptions<TValue> = Readonly<{
  mutate: ((idempotencyKey: string) => Promise<CommandResult<TValue>>) | undefined;
  onDone: ((value: TValue) => void) | undefined;
  onReload: (() => void) | undefined;
  onFailureReload: ((failure: CommandFailure) => void) | undefined;
  successMessage: string | undefined;
}>;

/** The state of one command with the rules of the header: one key, a `mounted` guard, renewal of the key, and a ref for the pending state. */
function useCommandRunner<TValue>(options: RunnerOptions<TValue>) {
  const [failure, setFailure] = useState<CommandFailure | null>(null);
  const [notDone, setNotDone] = useState<NotDone | null>(null);
  const [pending, setPending] = useState(false);
  // A ref as well as the state: Escape can arrive before the next render shows the pending state.
  const pendingRef = useRef(false);
  const key = useRef<string | null>(null);
  // An answer that arrives after the dialog is gone belongs to a dialog nobody sees: it must not close or navigate a newer one.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const latest = useRef(options);
  latest.current = options;

  const submit = useCallback(async () => {
    const { mutate } = latest.current;
    if (pendingRef.current || mutate === undefined) return;
    key.current ??= newKey();
    pendingRef.current = true;
    setPending(true);
    setFailure(null);
    setNotDone(null);
    let result: CommandResult<TValue>;
    try {
      result = await mutate(key.current);
    } catch {
      // `mutate` should resolve with a failure. A thrown error is an unexpected one: it may have run, so the same key can be retried.
      result = {
        ok: false,
        failure: { message: "The command could not be completed.", retryable: true },
      };
    }
    pendingRef.current = false;
    if (!mounted.current) return;
    setPending(false);
    const { onDone, onReload, onFailureReload, successMessage } = latest.current;
    if (result.ok) {
      if (successMessage) toast.success(successMessage);
      onDone?.(result.value);
    } else if ("notDone" in result) {
      // The command ran and did not do its job: the next try is another command.
      key.current = null;
      setNotDone(result.notDone);
      onReload?.();
    } else {
      const { failure: refused } = result;
      setFailure(refused);
      // The next body may differ (a refused field is fixed): it is another command, so it gets another key. A failure that may be
      // retried keeps the key and the body.
      if (refused.rotateKey === true || (refused.keepForm === true && refused.retryable !== true)) {
        key.current = null;
      }
      if (refused.reload === true) onFailureReload?.(refused);
    }
  }, []);

  const clearFailure = useCallback(() => setFailure(null), []);
  const controller: CommandController = { failure, notDone, pending, submit, clearFailure };
  return { controller, pendingRef };
}

export function CommandDialog<TValue = void>(props: CommandDialogProps<TValue>) {
  const {
    title,
    description,
    endedDescription,
    confirmLabel,
    pendingLabel = "Working…",
    retryLabel = "Try again",
    notDoneRetryLabel,
    cancelLabel = "Cancel",
    closeLabel = "Close",
    tone = "primary",
    order = "cancel-first",
    children,
    validate,
    confirmDisabled = false,
    confirmDisabledReason,
    stale = false,
    staleMessage = "This screen changed while this window was open. Nothing was sent. Close this window to read the latest, then make the change again.",
    titleFor,
    clearFailureOn = "never",
    footerNote,
    onFailureReload,
    onReload,
    onClose,
    successMessage,
    dialogClassName,
    focusTitle = false,
    focusFailure = false,
  } = props;

  const runner = useCommandRunner<TValue>({
    mutate: props.mutate,
    onDone: props.onDone,
    onReload,
    onFailureReload,
    successMessage,
  });
  const controller = props.controller ?? runner.controller;
  const { failure, pending } = controller;
  const notDone = controller.notDone ?? null;
  const pendingNow = () => (props.controller ? controller.pending : runner.pendingRef.current);

  const form = useRef<HTMLFormElement>(null);
  const confirmButton = useRef<HTMLButtonElement>(null);
  const alertRef = useRef<HTMLParagraphElement>(null);
  const reasonId = useId();
  const titleId = useId();
  const [attempt, setAttempt] = useState(0);

  // After a failed check the first invalid control takes focus. The effect runs after the error state is rendered.
  useEffect(() => {
    if (attempt > 0) focusFirstInvalid(form.current);
  }, [attempt]);

  // Nothing more can be done in this dialog: the person closes it.
  const terminal = failure !== null && !failure.retryable && failure.keepForm !== true;
  // The same body goes with the same key on a retry, so the fields stay as they were.
  const locked = failure?.retryable === true;
  const staleNow = stale && !pending && !terminal;
  const ended = terminal || staleNow;

  // The command starts: the fields are about to be disabled, and a disabled control that has focus loses it. Focus goes to the confirm
  // button, which stays focusable.
  useEffect(() => {
    if (!pending) return;
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      form.current?.contains(active) &&
      active !== confirmButton.current
    ) {
      confirmButton.current?.focus();
    }
  }, [pending]);

  // The dialog ended (a final failure, a stale screen): the confirm button is gone, so focus goes to the message that says why.
  useEffect(() => {
    if (ended) alertRef.current?.focus();
  }, [ended]);

  // `focusFailure`: a dialog whose fields stay open after a refusal also moves focus to the message (the person reads it, then fixes the field).
  useEffect(() => {
    if (focusFailure && (failure !== null || notDone !== null)) alertRef.current?.focus();
  }, [focusFailure, failure, notDone]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (pendingNow() || ended || confirmDisabled) return;
    if (validate && !validate()) {
      setAttempt((count) => count + 1);
      return;
    }
    void controller.submit();
  }

  const guardedClose = () => {
    if (!pendingNow()) onClose();
  };

  const shown: (CommandFailure | NotDone) | null = failure ?? notDone;
  const shownTitle = titleFor?.({ failure, stale: staleNow }) ?? title;
  const shownDescription = ended && endedDescription !== undefined ? endedDescription : description;
  const confirmText = pending
    ? pendingLabel
    : failure?.retryable
      ? retryLabel
      : notDone
        ? (notDoneRetryLabel ?? retryLabel)
        : confirmLabel;
  const cancelText = ended || notDone ? closeLabel : cancelLabel;
  const showReason = confirmDisabled && confirmDisabledReason !== undefined && !ended;

  const alertMessage = staleNow
    ? { tone: "conflict" as const, text: staleMessage }
    : shown === null
      ? null
      : {
          tone: failure?.tone ?? ("error" as const),
          text: (
            <>
              {shown.message}
              {shown.hint ? <> {shown.hint}</> : null}
              {shown.footerNote ? (
                <span className="mt-1 block text-xs opacity-90">{shown.footerNote}</span>
              ) : null}
            </>
          ),
        };

  const cancel = (
    <Button type="button" variant="secondary" onClick={guardedClose} disabled={pending}>
      {cancelText}
    </Button>
  );
  const confirm = ended ? null : (
    <Button
      ref={confirmButton}
      type="submit"
      loading={pending}
      disabled={confirmDisabled}
      aria-describedby={showReason ? reasonId : undefined}
      variant={tone === "danger" ? "destructive" : "default"}
    >
      {confirmText}
    </Button>
  );

  const state: CommandDialogState = { pending, locked };

  return (
    <Dialog
      title={shownTitle}
      titleId={titleId}
      description={shownDescription}
      onClose={guardedClose}
      className={dialogClassName}
      focusTitle={focusTitle}
    >
      <form
        ref={form}
        noValidate
        onSubmit={submit}
        onChange={() => {
          if (clearFailureOn === "edit" && failure !== null && !locked && !terminal) {
            controller.clearFailure?.();
          }
        }}
        className="grid gap-4"
        // Named by the title element, not by a copy of the title in an attribute: a title can hold a name (error reports copy attributes).
        aria-labelledby={titleId}
      >
        {ended ? null : (
          // Layout-neutral (`contents`): the fieldset only carries `disabled` for every field inside it.
          <fieldset disabled={pending || locked} className="contents m-0 min-w-0 border-0 p-0">
            {typeof children === "function" ? children(state) : children}
          </fieldset>
        )}
        {showReason ? (
          <p id={reasonId} className="m-0 text-sm leading-6 text-muted-foreground">
            {confirmDisabledReason}
          </p>
        ) : null}
        <StatusMessage message={alertMessage} focusRef={alertRef} />
        <div className="flex flex-wrap gap-2">
          {order === "cancel-first" ? (
            <>
              {cancel}
              {confirm}
            </>
          ) : (
            <>
              {confirm}
              {cancel}
            </>
          )}
        </div>
        {footerNote ? (
          <p className="m-0 text-xs leading-5 text-muted-foreground">{footerNote}</p>
        ) : null}
      </form>
    </Dialog>
  );
}
